/**
 * Клиент админки. Токен админа лежит в sessionStorage, а не в cookie:
 * админка открывается на отдельном домене и живёт в рамках вкладки — так
 * забытая открытая сессия не переживает закрытие браузера.
 */
const TOKEN_KEY = 'bonus_admin_token';

export const auth = {
  get token(): string | null {
    return sessionStorage.getItem(TOKEN_KEY);
  },
  set(token: string) {
    sessionStorage.setItem(TOKEN_KEY, token);
  },
  clear() {
    sessionStorage.removeItem(TOKEN_KEY);
  },
};

export class RequestFailed extends Error {
  constructor(readonly code: string, message: string, readonly details?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(auth.token ? { authorization: `Bearer ${auth.token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 401) {
    auth.clear();
    location.reload();
    throw new RequestFailed('UNAUTHORIZED', 'Сессия истекла');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code: string; message: string; details?: unknown } } | null;
    throw new RequestFailed(body?.error?.code ?? 'INTERNAL', body?.error?.message ?? `Ошибка ${res.status}`, body?.error?.details);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  return (ct.includes('json') ? await res.json() : await res.text()) as T;
}

// ─────────────────────────── Типы настроек ───────────────────────────

export type AttributionRule = {
  orderType: string;
  accrues: boolean;
  ratePerMille: number;
  minOrderAmountKopecks: number;
  pendingClarification: boolean;
  comment: string;
};

export type GradeTier = { fromOrdinal: number; toOrdinal: number | null; bonuses: number };

export type PlatformConfig = {
  attribution: {
    rules: AttributionRule[];
    refererResolution: 'AS_REPORTED' | 'FIRST_TOUCH' | 'LAST_TOUCH';
    forbidSelfReferral: boolean;
    quarantineUnknownOrderTypes: boolean;
  };
  accrual: {
    gradeScale: GradeTier[];
    counterScope: 'ALL_TIME' | 'ACADEMIC_YEAR';
    counterAdvance: 'ON_RELEASE' | 'ON_PAID';
    tierBasis: 'UNIQUE_REFEREE' | 'QUALIFYING_ORDER';
    holdDays: number;
    clawbackPolicy: 'ALLOW_NEGATIVE' | 'CLAMP_TO_ZERO' | 'MANUAL_ONLY';
    academicYearStart: { month: number; day: number };
  };
  shop: {
    globalMinPriceBonuses: number;
    reservationTtlMinutes: number;
    promoTtlDays: number;
    promoProvider: 'POOL' | 'ON_DEMAND';
  };
  identity: { sessionTtlHours: number; handoffEnabled: boolean; handoffTtlSeconds: number };
};

export type ConfigState = {
  current: { version: number; config: PlatformConfig; publishedAt: string };
  draft: { config: PlatformConfig; updatedBy: string; updatedAt: string };
  diff: { path: string; from: unknown; to: unknown }[];
};

export type Balance = { userId: string; pending: number; hold: number; available: number; inDebt: boolean };

export type AdminUser = {
  id: string | null;
  external_user_id: string;
  vk_id: string | null;
  display_name: string | null;
  referral_code: string | null;
  is_blocked: boolean;
  last_login_at: string | null;
  has_account?: boolean;
  balance: Balance | null;
  legacy_balance?: number | null;

  total_orders?: number;
  paid_orders?: number;
  eligible_orders?: number;
  confirmed_referees?: number;
  held_orders?: number;
  available_orders?: number;
  rejected_orders?: number;
  reversed_orders?: number;
  held_bonuses?: number;
  available_bonuses?: number;
  last_order_at?: string | null;
};

export type Category = {
  id: string;
  slug: string;
  title: string;
  value_outflow: boolean;
  min_price_bonuses: number;
  min_price_strict: boolean;
  sort_order: number;
};

export type Product = {
  id: string;
  category_id: string;
  category_title: string;
  sku: string;
  title: string;
  description: string;
  price_bonuses: number;
  delivery_kind: string;
  promo_nominal_kopecks: number | null;
  per_user_limit: number | null;
  is_active: boolean;
  sort_order: number;
  stock_available: number;
  stock_issued: number;
};


export type AnalyticsItem = {
  external_order_id: string;
  referrer_external_id: string | null;
  referee_external_id: string;
  referral_code: string | null;
  order_type: string;
  order_status: string;
  amount_kopecks: number;
  placed_at: string;
  paid_at: string | null;
  reversed_at: string | null;
  product_external_id: string | null;
  accrual_id: string | null;
  referrer_user_id: string | null;
  accrual_state: string | null;
  bonus_amount: number | null;
  ordinal: number | null;
  reject_reason: string | null;
  release_at: string | null;
  released_at: string | null;
  config_version: number | null;
};

export type AnalyticsResponse = {
  period: { from: string; to: string };
  summary: {
    totalOrders: number;
    paidOrders: number;
    eligibleOrders: number;
    pendingOrders: number;
    heldOrders: number;
    availableOrders: number;
    rejectedOrders: number;
    quarantinedOrders: number;
    reversedOrders: number;
    heldBonuses: number;
    availableBonuses: number;
    reversedBonuses: number;
  };
  total: number;
  items: AnalyticsItem[];
  byOrderType: {
    orderType: string;
    orders: number;
    paid: number;
    eligible: number;
    rejected: number;
    activeBonuses: number;
  }[];
};

export const api = {
  login: (login: string, password: string) =>
    request<{ token: string; admin: { id: string; displayName: string; scopes: string[] } }>('/auth/admin/login', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    }),

  config: () => request<ConfigState>('/api/admin/config'),
  saveDraft: (config: PlatformConfig) =>
    request<{ config: PlatformConfig; diff: ConfigState['diff'] }>('/api/admin/config/draft', {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
  publish: (comment: string) =>
    request<{ version: number }>('/api/admin/config/publish', { method: 'POST', body: JSON.stringify({ comment }) }),
  versions: () =>
    request<{ items: { version: number; published_by: string; comment: string; published_at: string; is_current: boolean }[] }>(
      '/api/admin/config/versions',
    ),
  rollback: (version: number) =>
    request<{ version: number }>('/api/admin/config/rollback', { method: 'POST', body: JSON.stringify({ version }) }),

  users: (
    query: string,
    limit = 25,
    offset = 0,
    sortBy?: string,
    sortDir: 'asc' | 'desc' = 'desc',
  ) => {
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });

    if (query) qs.set('query', query);

    if (sortBy) {
      qs.set('sortBy', sortBy);
      qs.set('sortDir', sortDir);
    }

    return request<{ total: number; items: AdminUser[] }>(
      `/api/admin/users?${qs}`,
    );
  },
  user: (id: string) =>
    request<{ user: AdminUser; balance: Balance; summary: Record<string, unknown> }>(`/api/admin/users/${id}`),
  userAccruals: (id: string) => request<{ items: Record<string, unknown>[] }>(`/api/admin/users/${id}/accruals`),
  userTransactions: (id: string) => request<{ items: Record<string, unknown>[] }>(`/api/admin/users/${id}/transactions`),
  adjust: (id: string, amount: number, reason: string, allowNegative: boolean) =>
    request<{ transactionId: string; before: Balance; after: Balance }>(`/api/admin/users/${id}/adjust`, {
      method: 'POST',
      body: JSON.stringify({ amount, reason, allowNegative }),
    }),
  block: (id: string, blocked: boolean, reason: string | null) =>
    request<{ ok: boolean }>(`/api/admin/users/${id}/block`, { method: 'POST', body: JSON.stringify({ blocked, reason }) }),

  categories: () => request<{ items: Category[] }>('/api/admin/shop/categories'),
  saveCategory: (id: string, patch: Partial<Category>) =>
    request<{ category: Category; violatingProducts: { id: string; title: string; price_bonuses: number }[] }>(
      `/api/admin/shop/categories/${id}`,
      { method: 'PATCH', body: JSON.stringify(camelize(patch)) },
    ),
  products: () => request<{ items: Product[] }>('/api/admin/shop/products'),
  createProduct: (body: Record<string, unknown>) =>
    request<Product>('/api/admin/shop/products', { method: 'POST', body: JSON.stringify(body) }),
  updateProduct: (id: string, body: Record<string, unknown>) =>
    request<Product>(`/api/admin/shop/products/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  uploadCodes: (id: string, codes: string[]) =>
    request<{ received: number; added: number; duplicates: number }>(`/api/admin/shop/products/${id}/codes`, {
      method: 'POST',
      body: JSON.stringify({ codes }),
    }),

  quarantine: () => request<{ items: Record<string, unknown>[] }>('/api/admin/quarantine'),
  resolveQuarantine: (orderId: string) =>
    request<{ ok: boolean }>(`/api/admin/quarantine/${encodeURIComponent(orderId)}/resolve`, { method: 'POST', body: '{}' }),
  replay: () => request<{ replayed: number; candidates: number }>('/api/admin/accruals/replay', { method: 'POST', body: '{}' }),

  analytics: (p: {
    from: string;
    to: string;
    state?: string;
    orderType?: string;
    query?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams({
      from: p.from,
      to: p.to,
      limit: String(p.limit ?? 50),
      offset: String(p.offset ?? 0),
    });
    if (p.state) qs.set('state', p.state);
    if (p.orderType) qs.set('orderType', p.orderType);
    if (p.query) qs.set('query', p.query);
    return request<AnalyticsResponse>(`/api/admin/analytics?${qs}`);
  },

  reports: (from: string, to: string) =>
    request<{
      period: Record<string, number>;
      liability: Record<string, number>;
      purchases: { items: { product_title: string; purchases: number; bonuses_spent: number }[] };
    }>(`/api/admin/reports/summary?from=${from}&to=${to}`),

  content: () => request<{ items: Record<string, unknown>[] }>('/api/admin/content'),
  saveContent: (key: string, body: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/api/admin/content/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  audit: (limit = 50) => request<{ items: Record<string, unknown>[] }>(`/api/admin/audit?limit=${limit}`),
  syncStatus: () => request<Record<string, unknown>>('/api/admin/sync/status'),
};

/** Патчи категорий приходят из формы в snake_case, а API ждёт camelCase. */
function camelize(o: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(o).map(([k, v]) => [k.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()), v]),
  );
}

export const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);
export const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—';
